import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, query, orderBy, getDoc, where, limit, deleteDoc, writeBatch, onSnapshot, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';
import { NotificationService } from '../../../utils/notificationService';
import { UserCreationService } from '../../../utils/userCreationService';
import { sendPasswordResetEmail, deleteUser, getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../../firebase';
import { getAuthContinueUrlBase, getEstablecerPasswordContinueUrl } from '../../../constants';
import { handleSolicitudDuplicateEmailAgent, DUPLICATE_EMAIL_CODE } from '../../repuestos/services/solicitudDuplicateEmailService';

export default function AdminSolicitudesCliente() {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [procesandoEtapa, setProcesandoEtapa] = useState(false);
  const [verificandoAuth, setVerificandoAuth] = useState(false);
  const [estadoAuth, setEstadoAuth] = useState(null);
  const [activandoSolicitud, setActivandoSolicitud] = useState(false);
  const [mostrarConfirmacionActivacion, setMostrarConfirmacionActivacion] = useState(false);
  const [botonesPresionados, setBotonesPresionados] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [eliminandoCliente, setEliminandoCliente] = useState(false);
  const [showConfirmacionEliminar, setShowConfirmacionEliminar] = useState(false);
  const [abrirWhatsAppDespuesActivacion, setAbrirWhatsAppDespuesActivacion] = useState(true);
  const [estadoClienteUsuario, setEstadoClienteUsuario] = useState(null);
  const [loadingEstadoCliente, setLoadingEstadoCliente] = useState(false);

  // Cargar estado del cliente (cambió contraseña, primer inicio sesión) desde usuarios cuando hay solicitud con usuario creado
  useEffect(() => {
    if (!solicitudSeleccionada || !db) {
      setEstadoClienteUsuario(null);
      return;
    }
    const uid = solicitudSeleccionada.uid_usuario || solicitudSeleccionada.uid;
    const email = solicitudSeleccionada.email?.trim?.() || solicitudSeleccionada.email;
    if (!uid && !email) {
      setEstadoClienteUsuario(null);
      return;
    }
    let mounted = true;
    setLoadingEstadoCliente(true);
    (async () => {
      try {
        let data = null;
        if (uid) {
          const ref = doc(db, 'usuarios', uid);
          const snap = await getDoc(ref);
          if (snap.exists()) data = snap.data();
        }
        if (!data && email) {
          const q = query(collection(db, 'usuarios'), where('email', '==', email), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) data = snap.docs[0].data();
        }
        if (!mounted) return;
        if (data) {
          setEstadoClienteUsuario({
            passwordEstablecidaAt: data.passwordEstablecidaAt ?? null,
            primerInicioSesionAt: data.primerInicioSesionAt ?? null
          });
        } else {
          setEstadoClienteUsuario(null);
        }
      } catch (err) {
        if (mounted) setEstadoClienteUsuario(null);
      } finally {
        if (mounted) setLoadingEstadoCliente(false);
      }
    })();
    return () => { mounted = false; };
  }, [solicitudSeleccionada?.id, solicitudSeleccionada?.uid_usuario, solicitudSeleccionada?.uid, solicitudSeleccionada?.email]);

  useEffect(() => {
    // Configurar listener en tiempo real para solicitudes
    let unsubscribe;
    
    try {
      const q = query(
        collection(db, 'solicitudes_cliente'),
        orderBy('fecha_solicitud', 'desc')
      );
      
      // Usar onSnapshot para actualización en tiempo real
      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const solicitudesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setSolicitudes(solicitudesData);
          setLoading(false);
          console.log('✅ Solicitudes actualizadas en tiempo real:', solicitudesData.length);
        },
        (error) => {
          console.error('❌ Error en listener de solicitudes:', error);
          setLoading(false);
          
          // Si falla el orderBy, intentar sin orden
          if (error.code === 'failed-precondition' || error.message?.includes('index')) {
            console.log('⚠️ Intentando cargar sin orderBy...');
            const qSimple = collection(db, 'solicitudes_cliente');
            unsubscribe = onSnapshot(
              qSimple,
              (snapshot) => {
                const solicitudesData = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                // Ordenar manualmente por fecha
                solicitudesData.sort((a, b) => {
                  const fechaA = a.fecha_solicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
                  const fechaB = b.fecha_solicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
                  return fechaB - fechaA;
                });
                setSolicitudes(solicitudesData);
                setLoading(false);
              },
              (error2) => {
                console.error('❌ Error en listener simple:', error2);
                setLoading(false);
              }
            );
          }
        }
      );
    } catch (error) {
      console.error('❌ Error configurando listener:', error);
      setLoading(false);
    }
    
    // Limpiar listener al desmontar componente
    return () => {
      if (unsubscribe) {
        unsubscribe();
        console.log('🧹 Listener de solicitudes limpiado');
      }
    };
  }, []);

  // Función para recargar manualmente (opcional, para botón de refresh)
  const cargarSolicitudes = async () => {
    try {
      const q = query(
        collection(db, 'solicitudes_cliente'),
        orderBy('fecha_solicitud', 'desc')
      );
      const snapshot = await getDocs(q);
      const solicitudesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSolicitudes(solicitudesData);
      console.log('✅ Solicitudes recargadas manualmente');
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  /** Normaliza teléfono para Chile y devuelve URL de WhatsApp (wa.me) con mensaje prellenado. */
  const buildWhatsAppUrl = (telefono, mensaje) => {
    if (!telefono || !String(telefono).trim()) return null;
    const digits = String(telefono).replace(/\D/g, '');
    let num = digits;
    if (num.length === 9 && num.startsWith('9')) num = '56' + num;
    else if (num.length === 8) num = '569' + num;
    else if (!num.startsWith('56')) num = '56' + num;
    if (num.length < 11) return null;
    const text = encodeURIComponent(mensaje || '');
    return `https://wa.me/${num}${text ? `?text=${text}` : ''}`;
  };

  const activarSolicitud = async (solicitud) => {
    if (!solicitud) return;
    
    // Validar que la etapa de validación de datos esté completada
    const validacionDatos = solicitud.etapas?.validacion_datos;
    if (!validacionDatos || validacionDatos.estado !== 'completada') {
      alert('⚠️ Debes completar primero la etapa de "Validación de Datos" antes de activar la solicitud.');
      return;
    }

    // Validar que no esté ya activada
    if (solicitud.usuario_creado) {
      alert('ℹ️ Esta solicitud ya ha sido activada. El usuario ya existe en el sistema.');
      return;
    }

    setMostrarConfirmacionActivacion(true);
  };

  /** Escribe membresía de prueba gratuita (30 días) a usuarios/{uid} si no tiene una. */
  const escribirTrialSiNoExiste = async (uid, adminEmail) => {
    try {
      const snap = await getDoc(doc(db, 'usuarios', uid));
      if (!snap.exists() || snap.data().membresia) return; // ya tiene membresía
      const ahora = new Date();
      const fechaFin = new Date(ahora);
      fechaFin.setDate(fechaFin.getDate() + 30);
      const fechaFinTs = Timestamp.fromDate(fechaFin);
      const membresiaMap = {
        tipo: 'prueba_gratuita',
        estado: 'activa',
        fechaInicio: Timestamp.fromDate(ahora),
        fechaFin: fechaFinTs,
        dias: 30,
        avisadoVencimiento: false,
      };
      await updateDoc(doc(db, 'usuarios', uid), { membresia: membresiaMap });
      // Registro en colección membresias para que verificarMembresiasVencidas lo encuentre
      await addDoc(collection(db, 'membresias'), {
        userId: uid,
        tipo: 'prueba_gratuita',
        estado: 'activa',
        fechaInicio: serverTimestamp(),
        fechaFin: fechaFinTs,
        dias: 30,
        metodoPago: 'gratuito',
        adminId: adminEmail || 'admin',
        creadoEn: serverTimestamp(),
      });
    } catch (e) {
      console.warn('No se pudo escribir trial de membresía:', e);
    }
  };

  /** Recarga el documento de la solicitud desde Firestore para actualizar el Detalle (email/WhatsApp enviado, etc.). */
  const refetchSolicitudDetalle = async (solicitudId) => {
    if (!solicitudId) return null;
    const snap = await getDoc(doc(db, 'solicitudes_cliente', solicitudId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  };

  const confirmarActivacion = async () => {
    if (!solicitudSeleccionada) return;
    
    setMostrarConfirmacionActivacion(false);
    setActivandoSolicitud(true);
    
    try {
      const solicitud = solicitudSeleccionada;
      const etapasActualizadas = { ...solicitud.etapas };
      
      // Completar ambas etapas
      etapasActualizadas['validacion_datos'] = {
        ...etapasActualizadas['validacion_datos'],
        estado: 'completada',
        fecha_fin: new Date(),
        responsable: user.email
      };
      
      etapasActualizadas['confirmacion_final'] = {
        ...etapasActualizadas['confirmacion_final'],
        estado: 'completada',
        fecha_fin: new Date(),
        responsable: user.email
      };

      // Si el usuario ya fue creado en el registro (onboarding con password), solo activar
      if (solicitud.uid) {
        await updateDoc(doc(db, 'usuarios', solicitud.uid), {
          status: 'activo',
          approvedAt: serverTimestamp(),
          approvedBy: user.email
        });
        // Si viene del flujo básico (dashboard-basico),
        // actualizar estadoInicial y asegurar rol cliente
        const { getDoc } = await import('firebase/firestore');
        const usuarioSnap = await getDoc(doc(db, 'usuarios', solicitud.uid));
        if (usuarioSnap.exists() && usuarioSnap.data().estadoInicial === true) {
          await updateDoc(doc(db, 'usuarios', solicitud.uid), {
            estadoInicial: false,
            activo: true,
            rol: 'cliente',
            activadoEn: serverTimestamp(),
          });
          try {
            const perfilBasicoRef = doc(db, 'perfiles_basicos', solicitud.uid);
            const perfilSnap = await getDoc(perfilBasicoRef);
            if (perfilSnap.exists()) {
              await updateDoc(perfilBasicoRef, {
                activado: true,
                activadoEn: serverTimestamp(),
              });
            }
          } catch (e) {}
        }
        await updateDoc(doc(db, 'solicitudes_cliente', solicitud.id), {
          etapas: etapasActualizadas,
          progreso_porcentaje: 100,
          estado_general: 'aprobada',
          etapa_actual: 'confirmacion_final',
          fecha_activacion: new Date(),
          activado_por: user.email,
          usuario_creado: true,
          uid_usuario: solicitud.uid
        });
        await cargarSolicitudes();
        setSolicitudSeleccionada({ ...solicitud, estado_general: 'aprobada', uid_usuario: solicitud.uid, etapas: etapasActualizadas });
        const loginUrlExistente = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin.replace(/\/$/, '') + '/login' : 'https://kawescar.cl/login';
        const mensajeWaExistente = `¡Felicidades! Tu cuenta en KawesCar ya está activa. 🎉 Ya eres parte de la comunidad.\n\nIngresa aquí para acceder: ${loginUrlExistente}\n\nSaludos, equipo KawesCar`;
        if (abrirWhatsAppDespuesActivacion && solicitud.telefono) {
          const urlWa = buildWhatsAppUrl(solicitud.telefono, mensajeWaExistente);
          if (urlWa) {
            window.open(urlWa, '_blank', 'noopener,noreferrer');
            const waFecha = new Date();
            await updateDoc(doc(db, 'solicitudes_cliente', solicitud.id), { whatsapp_activacion_enviado: true, whatsapp_activacion_fecha: waFecha });
          }
        }
        await escribirTrialSiNoExiste(solicitud.uid, user.email);
        const actualizada = await refetchSolicitudDetalle(solicitud.id);
        if (actualizada) setSolicitudSeleccionada(actualizada);
        alert('✅ Solicitud aprobada. Se enviará un email al cliente indicando que su cuenta ya está activa (si está configurada la API de Resend en Firebase).' + (solicitud.telefono && abrirWhatsAppDespuesActivacion ? '\n\n📱 Se abrió WhatsApp con el mensaje para el cliente (incluye enlace de login).' : ''));
        setActivandoSolicitud(false);
        return;
      }

      // Crear el usuario (flujo legacy: sin cuenta al registrar)
      try {
        const guard = await handleSolicitudDuplicateEmailAgent({
          operation: 'precheck_before_activation',
          collectionName: 'solicitudes_cliente',
          solicitudId: solicitud.id,
        });
        if (guard.code === DUPLICATE_EMAIL_CODE || (guard.handled === true && guard.ok === false)) {
          await cargarSolicitudes();
          const act = await refetchSolicitudDetalle(solicitud.id);
          if (act) setSolicitudSeleccionada(act);
          alert(
            guard.message ||
              'La solicitud fue rechazada porque el correo ya se encuentra registrado en el sistema. Se notificó al solicitante por correo cuando fue posible.'
          );
          setActivandoSolicitud(false);
          return;
        }
      } catch (ge) {
        console.error('Precheck correo duplicado:', ge);
        alert('No se pudo verificar el correo en el servidor. Inténtalo de nuevo.');
        setActivandoSolicitud(false);
        return;
      }

      const resultadoCreacion = await UserCreationService.createClientUser({
        ...solicitud,
        id: solicitud.id
      });

      if (resultadoCreacion.success) {
        // Preparar datos de actualización
        const datosActualizacion = {
          etapas: etapasActualizadas,
          progreso_porcentaje: 100,
          estado_general: 'aprobada',
          etapa_actual: 'confirmacion_final',
          usuario_creado: true,
          uid_usuario: resultadoCreacion.uid,
          fecha_activacion: new Date(),
          perfil_id: resultadoCreacion.profileId,
          activado_por: user.email,
          fecha_activacion_admin: new Date()
        };

        // Si se generó una contraseña temporal, guardarla en la solicitud
        if (resultadoCreacion.isTemporaryPassword && resultadoCreacion.temporaryPassword) {
          datosActualizacion.password_temporal_generado = true;
          datosActualizacion.password_temporal = resultadoCreacion.temporaryPassword;
          datosActualizacion.nota_password_temporal = 'Se generó una contraseña temporal porque la solicitud no tenía password_hash. El cliente debe usar esta contraseña para iniciar sesión y luego cambiarla.';
          
          alert(`⚠️ Se generó una contraseña temporal para el usuario.\n\nEmail: ${resultadoCreacion.email}\nContraseña temporal: ${resultadoCreacion.temporaryPassword}\n\nIMPORTANTE: Comunica esta contraseña al cliente de forma segura.`);
        }

        // Enviar email de activación con link para establecer contraseña
        let emailEnviado = false;
        let emailError = null;
        let tipoEmailEnviado = null;
        
        try {
          // Si el usuario tiene contraseña temporal, enviar email para establecer nueva contraseña
          if (resultadoCreacion.isTemporaryPassword) {
            // Usar Firebase Auth para enviar link de restablecimiento de contraseña
            const actionCodeSettings = {
              url: getEstablecerPasswordContinueUrl(resultadoCreacion.email),
              handleCodeInApp: false,
            };
            
            await sendPasswordResetEmail(auth, resultadoCreacion.email, actionCodeSettings);
            emailEnviado = true;
            tipoEmailEnviado = 'password_reset';
            console.log('✅ Email de activación enviado para establecer contraseña');
          } else {
            // Si ya tiene contraseña, enviar email de bienvenida con link de login
            try {
              const functions = getFunctions();
              const enviarEmailActivacion = httpsCallable(functions, 'enviarEmailActivacionCliente');
              
              const resultadoEmail = await enviarEmailActivacion({
                email: resultadoCreacion.email,
                nombre: `${solicitud.nombres} ${solicitud.apellidos}`,
                uid: resultadoCreacion.uid,
                loginUrl: `${getAuthContinueUrlBase()}/login`
              });
              
              // Verificar que la Cloud Function respondió correctamente
              if (resultadoEmail && resultadoEmail.data && resultadoEmail.data.success !== false) {
                emailEnviado = true;
                tipoEmailEnviado = 'bienvenida';
                console.log('✅ Email de bienvenida enviado');
              } else {
                throw new Error('Cloud Function no retornó éxito');
              }
            } catch (emailErrorFunc) {
              console.warn('⚠️ No se pudo enviar email de bienvenida (Cloud Function no disponible):', emailErrorFunc);
              emailError = emailErrorFunc.message || 'Cloud Function no disponible';
              // Intentar con sendPasswordResetEmail como fallback
              try {
                await sendPasswordResetEmail(auth, resultadoCreacion.email);
                emailEnviado = true;
                tipoEmailEnviado = 'password_reset_fallback';
                console.log('✅ Email de restablecimiento enviado como fallback');
              } catch (fallbackError) {
                console.error('❌ Error en fallback de email:', fallbackError);
                emailError = fallbackError.message || 'Error al enviar email';
              }
            }
          }
        } catch (emailErrorGeneral) {
          console.error('⚠️ Error enviando email de activación:', emailErrorGeneral);
          emailError = emailErrorGeneral.message || 'Error desconocido al enviar email';
        }

        // Guardar estado del envío de email en la solicitud
        datosActualizacion.email_activacion_enviado = emailEnviado;
        datosActualizacion.email_activacion_fecha = emailEnviado ? new Date() : null;
        datosActualizacion.email_activacion_tipo = tipoEmailEnviado;
        if (emailError) {
          datosActualizacion.email_activacion_error = emailError;
          datosActualizacion.email_activacion_intentos = (solicitud.email_activacion_intentos || 0) + 1;
        }

        // Actualizar la solicitud con la información del usuario creado y estado del email
        await updateDoc(doc(db, 'solicitudes_cliente', solicitud.id), datosActualizacion);

        // Notificar al cliente que su cuenta está lista
        await NotificationService.createInAppNotification(
          resultadoCreacion.uid,
          'validacion',
          '¡Cuenta activada! Bienvenido a KawesCar',
          'Tu cuenta de cliente ha sido creada exitosamente. Revisa tu email para establecer tu contraseña e iniciar sesión.',
          {
            solicitudId: solicitud.id,
            uid: resultadoCreacion.uid,
            email: resultadoCreacion.email,
            origen: 'cuenta_activada'
          }
        );

        await escribirTrialSiNoExiste(resultadoCreacion.uid, user.email);

        // Recargar solicitudes
        await cargarSolicitudes();

        // Mensaje de confirmación con estado del email
        let mensajeEmail = '';
        if (emailEnviado) {
          mensajeEmail = `\n\n📧 ✅ Email enviado exitosamente al cliente (${tipoEmailEnviado === 'password_reset' || tipoEmailEnviado === 'password_reset_fallback' ? 'link para establecer contraseña' : 'bienvenida'})`;
        } else {
          mensajeEmail = `\n\n⚠️ ⚠️ ADVERTENCIA: No se pudo enviar el email al cliente.\n\nError: ${emailError || 'Error desconocido'}\n\nPor favor, comunica manualmente al cliente que su cuenta está activa y que puede iniciar sesión. Si se generó una contraseña temporal, compártela de forma segura.`;
        }

        // Abrir pantalla de WhatsApp con mensaje completo (cuenta activada + URL de login)
        const loginUrl = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin.replace(/\/$/, '') + '/login' : 'https://kawescar.cl/login';
        const mensajeWhatsApp = `¡Felicidades! Tu cuenta en KawesCar ya está activa. 🎉 Ya eres parte de la comunidad.\n\nSe ha enviado un correo a ${solicitud.email} para que puedas establecer tu contraseña e iniciar sesión. Revisa tu bandeja de entrada (y carpeta de spam).\n\nIngresa aquí para acceder a tu cuenta:\n${loginUrl}\n\nSaludos, equipo KawesCar`;
        if (abrirWhatsAppDespuesActivacion && solicitud.telefono) {
          const urlWhatsApp = buildWhatsAppUrl(solicitud.telefono, mensajeWhatsApp);
          if (urlWhatsApp) {
            window.open(urlWhatsApp, '_blank', 'noopener,noreferrer');
            const waFecha = new Date();
            await updateDoc(doc(db, 'solicitudes_cliente', solicitud.id), { whatsapp_activacion_enviado: true, whatsapp_activacion_fecha: waFecha });
            mensajeEmail += '\n\n📱 Se abrió WhatsApp con el mensaje para el cliente (incluye enlace de login).';
          }
        } else if (!solicitud.telefono) {
          mensajeEmail += '\n\n📱 No hay teléfono en la solicitud. Puedes copiar el mensaje y enviarlo manualmente por WhatsApp.';
        }
        setAbrirWhatsAppDespuesActivacion(true);

        const actualizada = await refetchSolicitudDetalle(solicitud.id);
        if (actualizada) setSolicitudSeleccionada(actualizada);

        alert(`✅ Solicitud activada exitosamente!\n\nUsuario creado para:\n${solicitud.nombres} ${solicitud.apellidos}\nEmail: ${solicitud.email}\nUID: ${resultadoCreacion.uid}${mensajeEmail}`);
      } else if (resultadoCreacion.code === 'auth/email-already-in-use') {
        try {
          const fin = await handleSolicitudDuplicateEmailAgent({
            operation: 'finalize_after_client_auth_error',
            collectionName: 'solicitudes_cliente',
            solicitudId: solicitud.id,
          });
          await cargarSolicitudes();
          const act = await refetchSolicitudDetalle(solicitud.id);
          if (act) setSolicitudSeleccionada(act);
          alert(
            fin.message ||
              'La solicitud fue rechazada porque el correo ya está registrado en el sistema.'
          );
        } catch (fe) {
          console.error('finalize duplicate cliente:', fe);
          alert(
            'El correo ya está registrado en el sistema. La solicitud debería rechazarse desde el panel; si no se actualizó, hazlo manualmente o contacta a soporte.'
          );
        }
      } else {
        const errorMessage = resultadoCreacion.error || 'Error desconocido';
        const errorCode = resultadoCreacion.code || 'unknown';
        console.error('❌ Error al crear usuario:', {
          error: errorMessage,
          code: errorCode,
          details: resultadoCreacion.details,
          solicitud: {
            id: solicitud.id,
            email: solicitud.email,
            tienePasswordHash: !!solicitud.password_hash,
            tipoPasswordHash: typeof solicitud.password_hash,
            longitudPasswordHash: solicitud.password_hash?.length
          }
        });
        alert(
          `No se pudo crear el usuario.\n\n${errorMessage}\n\nSi el problema es la contraseña guardada en la solicitud, revisa los datos o contacta a soporte.`
        );
      }
    } catch (error) {
      console.error('Error activando solicitud:', error);
      alert(`❌ Error al activar la solicitud: ${error.message}`);
    } finally {
      setActivandoSolicitud(false);
    }
  };

  const actualizarEtapa = async (solicitudId, etapaKey, nuevoEstado, comentarios = '') => {
    // Marcar el botón como presionado
    const botonKey = `${solicitudId}_${etapaKey}_${nuevoEstado}`;
    setBotonesPresionados(prev => ({
      ...prev,
      [botonKey]: true
    }));
    
    setProcesandoEtapa(true);
    try {
      const solicitud = solicitudes.find(s => s.id === solicitudId);
      const etapasActualizadas = { ...solicitud.etapas };
      
      // Actualizar la etapa actual
      etapasActualizadas[etapaKey] = {
        ...etapasActualizadas[etapaKey],
        estado: nuevoEstado,
        fecha_fin: nuevoEstado === 'completada' ? new Date() : null,
        comentarios,
        responsable: user.email
      };

      // Calcular nuevo progreso - Solo 2 etapas: validacion_datos y confirmacion_final
      const etapasOrdenadas = ['validacion_datos', 'confirmacion_final'];
      const etapasCompletadas = etapasOrdenadas.filter(key => 
        etapasActualizadas[key]?.estado === 'completada'
      ).length;
      const nuevoPorcentaje = Math.round((etapasCompletadas / etapasOrdenadas.length) * 100);

      // Determinar estado general
      let estadoGeneral = 'en_revision';
      if (nuevoEstado === 'rechazada') {
        estadoGeneral = 'rechazada';
      } else if (etapaKey === 'confirmacion_final' && nuevoEstado === 'completada') {
        estadoGeneral = 'aprobada';
      }

      // Si se aprueba la solicitud final, crear el usuario
      if (etapaKey === 'confirmacion_final' && nuevoEstado === 'completada') {
        try {
          const guard = await handleSolicitudDuplicateEmailAgent({
            operation: 'precheck_before_activation',
            collectionName: 'solicitudes_cliente',
            solicitudId,
          });
          if (guard.code === DUPLICATE_EMAIL_CODE || (guard.handled === true && guard.ok === false)) {
            await cargarSolicitudes();
            if (solicitudSeleccionada?.id === solicitudId) {
              const act = await refetchSolicitudDetalle(solicitudId);
              if (act) setSolicitudSeleccionada(act);
            }
            alert(
              guard.message ||
                'La solicitud fue rechazada porque el correo ya está registrado en el sistema.'
            );
            setProcesandoEtapa(false);
            return;
          }
        } catch (ge) {
          console.error('Precheck correo duplicado (etapa):', ge);
          alert('No se pudo verificar el correo en el servidor. Inténtalo de nuevo.');
          setProcesandoEtapa(false);
          return;
        }

        const resultadoCreacion = await UserCreationService.createClientUser({
          ...solicitud,
          id: solicitudId
        });

        if (resultadoCreacion.success) {
          // Actualizar la solicitud con la información del usuario creado
          await updateDoc(doc(db, 'solicitudes_cliente', solicitudId), {
            etapas: etapasActualizadas,
            progreso_porcentaje: 100,
            estado_general: 'aprobada',
            etapa_actual: 'confirmacion_final',
            usuario_creado: true,
            uid_usuario: resultadoCreacion.uid,
            fecha_activacion: new Date(),
            perfil_id: resultadoCreacion.profileId
          });

          // Notificar al cliente que su cuenta está lista
          await NotificationService.createInAppNotification(
            resultadoCreacion.uid,
            'validacion',
            '¡Cuenta activada! Bienvenido a KawesCar',
            'Tu cuenta de cliente ha sido creada exitosamente. Ya puedes iniciar sesión y gestionar tus vehículos.',
            {
              solicitudId,
              uid: resultadoCreacion.uid,
              email: resultadoCreacion.email,
              origen: 'cuenta_activada'
            }
          );

          alert(`Usuario creado exitosamente para ${solicitud.nombres} ${solicitud.apellidos}`);
        } else if (resultadoCreacion.code === 'auth/email-already-in-use') {
          try {
            const fin = await handleSolicitudDuplicateEmailAgent({
              operation: 'finalize_after_client_auth_error',
              collectionName: 'solicitudes_cliente',
              solicitudId,
            });
            await cargarSolicitudes();
            if (solicitudSeleccionada?.id === solicitudId) {
              const act = await refetchSolicitudDetalle(solicitudId);
              if (act) setSolicitudSeleccionada(act);
            }
            alert(
              fin.message ||
                'La solicitud fue rechazada porque el correo ya está registrado en el sistema.'
            );
          } catch (fe) {
            console.error(fe);
            alert(
              'El correo ya está registrado. Revisa la solicitud o recházala manualmente desde el panel.'
            );
          }
          setProcesandoEtapa(false);
          return;
        } else {
          alert(
            `No se pudo crear el usuario: ${resultadoCreacion.error || 'Error desconocido'}`
          );
          setProcesandoEtapa(false);
          return;
        }
      } else {
        // Actualización normal de etapa
        await updateDoc(doc(db, 'solicitudes_cliente', solicitudId), {
          etapas: etapasActualizadas,
          progreso_porcentaje: nuevoPorcentaje,
          estado_general: estadoGeneral,
          etapa_actual: etapaKey
        });
      }

      // Enviar notificación al cliente
      await NotificationService.createInAppNotification(
        solicitud.uid || solicitud.email,
        'validacion',
        'Actualización en tu solicitud',
        `La etapa "${etapaKey}" ha sido ${nuevoEstado === 'completada' ? 'completada' : 'actualizada'}`,
        {
          solicitudId,
          etapa: etapaKey,
          estado: nuevoEstado,
          origen: 'admin_solicitudes_cliente'
        }
      );

      // Recargar solicitudes
      await cargarSolicitudes();
      
      // Actualizar la solicitud seleccionada después de recargar
      if (solicitudSeleccionada && solicitudSeleccionada.id === solicitudId) {
        // Obtener la solicitud actualizada directamente de Firestore
        const solicitudDocRef = doc(db, 'solicitudes_cliente', solicitudId);
        const solicitudDocSnap = await getDoc(solicitudDocRef);
        if (solicitudDocSnap.exists()) {
          const solicitudActualizada = {
            id: solicitudDocSnap.id,
            ...solicitudDocSnap.data()
          };
          setSolicitudSeleccionada(solicitudActualizada);
        }
      }
      
      // Limpiar el estado del botón presionado después de un momento
      setTimeout(() => {
        setBotonesPresionados(prev => {
          const nuevo = { ...prev };
          delete nuevo[botonKey];
          return nuevo;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error updating stage:', error);
      alert('Error al actualizar la etapa: ' + error.message);
      // Remover el estado de presionado si hay error
      setBotonesPresionados(prev => {
        const nuevo = { ...prev };
        delete nuevo[botonKey];
        return nuevo;
      });
    } finally {
      setProcesandoEtapa(false);
    }
  };

  const verificarUsuarioEnAuth = async (email) => {
    setVerificandoAuth(true);
    setEstadoAuth(null);
    try {
      const resultado = await UserCreationService.checkUserExistsInAuth(email);
      setEstadoAuth({
        existe: resultado.exists,
        metodos: resultado.methods
      });
    } catch (error) {
      console.error('Error verificando usuario en Auth:', error);
      setEstadoAuth({
        existe: false,
        error: error.message
      });
    } finally {
      setVerificandoAuth(false);
    }
  };

  const eliminarClienteCompleto = async (solicitud) => {
    if (!solicitud) return;
    
    // Confirmación doble
    const confirmacion1 = window.confirm(
      `⚠️ ADVERTENCIA: Esta acción eliminará PERMANENTEMENTE:\n\n` +
      `• La solicitud del cliente\n` +
      `• El perfil completo del cliente\n` +
      `• El usuario de Firebase Authentication\n` +
      `• Todos los vehículos asociados\n` +
      `• Todos los documentos\n` +
      `• Todos los gastos\n` +
      `• Todas las actividades\n\n` +
      `¿Estás SEGURO de que deseas continuar?`
    );
    
    if (!confirmacion1) return;
    
    const confirmacion2 = window.confirm(
      `🔴 ÚLTIMA CONFIRMACIÓN\n\n` +
      `Vas a eliminar completamente a:\n` +
      `${solicitud.nombres} ${solicitud.apellidos}\n` +
      `${solicitud.email}\n\n` +
      `Esta acción NO se puede deshacer.\n\n` +
      `¿Confirmas la eliminación?`
    );
    
    if (!confirmacion2) return;
    
    setEliminandoCliente(true);
    
    try {
      console.log('🗑️ Iniciando eliminación completa del cliente:', solicitud.id);
      
      // Obtener el UID del cliente si existe
      let clienteUid = null;
      let perfilClienteId = null;
      
      // Buscar el perfil del cliente
      if (solicitud.usuario_creado && solicitud.uid) {
        clienteUid = solicitud.uid;
      } else {
        // Buscar por email en perfiles_clientes
        const perfilesQuery = query(
          collection(db, 'perfiles_clientes'),
          where('email', '==', solicitud.email)
        );
        const perfilesSnapshot = await getDocs(perfilesQuery);
        if (!perfilesSnapshot.empty) {
          const perfil = perfilesSnapshot.docs[0].data();
          clienteUid = perfil.uid || perfil.clienteId || perfil.userId;
          perfilClienteId = perfilesSnapshot.docs[0].id;
        }
      }
      
      // Si no encontramos UID, buscar en usuarios
      if (!clienteUid) {
        const usuariosQuery = query(
          collection(db, 'usuarios'),
          where('email', '==', solicitud.email.toLowerCase().trim())
        );
        const usuariosSnapshot = await getDocs(usuariosQuery);
        if (!usuariosSnapshot.empty) {
          const usuario = usuariosSnapshot.docs[0].data();
          clienteUid = usuario.uid;
        }
      }
      
      console.log('🔍 UID encontrado:', clienteUid);
      
      // Usar batch para eliminar múltiples documentos
      const batch = writeBatch(db);
      let documentosEliminados = 0;
      
      // 1. Eliminar solicitud_cliente
      const solicitudRef = doc(db, 'solicitudes_cliente', solicitud.id);
      batch.delete(solicitudRef);
      documentosEliminados++;
      console.log('✅ Solicitud marcada para eliminación');
      
      // 2. Eliminar perfil_cliente
      if (perfilClienteId) {
        const perfilRef = doc(db, 'perfiles_clientes', perfilClienteId);
        batch.delete(perfilRef);
        documentosEliminados++;
        console.log('✅ Perfil de cliente marcado para eliminación');
      } else if (clienteUid) {
        // Buscar perfil por UID
        const perfilesQuery = query(
          collection(db, 'perfiles_clientes'),
          where('uid', '==', clienteUid)
        );
        const perfilesSnapshot = await getDocs(perfilesQuery);
        perfilesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
          documentosEliminados++;
        });
        console.log('✅ Perfiles de cliente marcados para eliminación');
      }
      
      // 3. Eliminar usuario de la colección usuarios
      if (clienteUid) {
        const usuarioRef = doc(db, 'usuarios', clienteUid);
        const usuarioDoc = await getDoc(usuarioRef);
        if (usuarioDoc.exists()) {
          batch.delete(usuarioRef);
          documentosEliminados++;
          console.log('✅ Usuario marcado para eliminación');
        }
      }
      
      // 4. Eliminar vehículos del cliente
      if (clienteUid) {
        const vehiculosQuery = query(
          collection(db, 'vehiculos'),
          where('userId', '==', clienteUid)
        );
        const vehiculosSnapshot = await getDocs(vehiculosQuery);
        const vehiculosIds = [];
        vehiculosSnapshot.docs.forEach(vehiculoDoc => {
          vehiculosIds.push(vehiculoDoc.id);
          batch.delete(vehiculoDoc.ref);
          documentosEliminados++;
        });
        console.log(`✅ ${vehiculosIds.length} vehículos marcados para eliminación`);
        
        // 5. Eliminar documentos de vehículos
        for (const vehiculoId of vehiculosIds) {
          const documentosQuery = query(
            collection(db, 'documentos_vehiculos'),
            where('vehiculoId', '==', vehiculoId)
          );
          const documentosSnapshot = await getDocs(documentosQuery);
          documentosSnapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
            documentosEliminados++;
          });
        }
        console.log('✅ Documentos de vehículos marcados para eliminación');
        
        // 6. Eliminar gastos de vehículos
        for (const vehiculoId of vehiculosIds) {
          const gastosQuery = query(
            collection(db, 'gastos_vehiculos'),
            where('vehiculoId', '==', vehiculoId)
          );
          const gastosSnapshot = await getDocs(gastosQuery);
          gastosSnapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
            documentosEliminados++;
          });
        }
        console.log('✅ Gastos de vehículos marcados para eliminación');
        
        // 7. Eliminar datos de Boostr
        for (const vehiculoId of vehiculosIds) {
          const boostrQuery = query(
            collection(db, 'vehiculos_boostr_data'),
            where('vehiculoId', '==', vehiculoId)
          );
          const boostrSnapshot = await getDocs(boostrQuery);
          boostrSnapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
            documentosEliminados++;
          });
        }
        console.log('✅ Datos Boostr marcados para eliminación');
        
        // 8. Eliminar actividades del cliente
        const actividadesQuery = query(
          collection(db, 'actividades_diarias_clientes'),
          where('userId', '==', clienteUid)
        );
        const actividadesSnapshot = await getDocs(actividadesQuery);
        actividadesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
          documentosEliminados++;
        });
        console.log(`✅ ${actividadesSnapshot.size} actividades marcadas para eliminación`);
        
        // 9. Eliminar servicios realizados
        const serviciosQuery = query(
          collection(db, 'servicios_realizados'),
          where('clienteId', '==', clienteUid)
        );
        const serviciosSnapshot = await getDocs(serviciosQuery);
        serviciosSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
          documentosEliminados++;
        });
        console.log(`✅ ${serviciosSnapshot.size} servicios marcados para eliminación`);
        
        // 10. Eliminar recordatorios
        const recordatoriosQuery = query(
          collection(db, 'recordatorios'),
          where('userId', '==', clienteUid)
        );
        const recordatoriosSnapshot = await getDocs(recordatoriosQuery);
        recordatoriosSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
          documentosEliminados++;
        });
        console.log(`✅ ${recordatoriosSnapshot.size} recordatorios marcados para eliminación`);
      }
      
      // Ejecutar batch
      await batch.commit();
      console.log(`✅ Batch ejecutado: ${documentosEliminados} documentos eliminados de Firestore`);
      
      // 11. Eliminar usuario de Firebase Authentication
      if (clienteUid) {
        try {
          // Necesitamos usar Admin SDK para eliminar usuarios, pero podemos intentar con el cliente
          // En producción, esto debería hacerse desde una Cloud Function
          console.log('⚠️ Eliminación de Firebase Auth debe hacerse desde Cloud Function o Admin SDK');
          console.log('📝 UID a eliminar de Auth:', clienteUid);
          
          // Nota: deleteUser solo funciona si el usuario está autenticado como ese usuario
          // Para eliminar desde admin, necesitamos una Cloud Function
          alert(
            `✅ Eliminación de datos completada:\n\n` +
            `• ${documentosEliminados} documentos eliminados de Firestore\n` +
            `• Solicitud eliminada\n` +
            `• Perfil eliminado\n` +
            `• Vehículos y datos relacionados eliminados\n\n` +
            `⚠️ IMPORTANTE: Para eliminar el usuario de Firebase Authentication,\n` +
            `debes usar una Cloud Function con Admin SDK o eliminarlo manualmente\n` +
            `desde Firebase Console.\n\n` +
            `UID del usuario: ${clienteUid}`
          );
        } catch (authError) {
          console.error('Error eliminando usuario de Auth:', authError);
          alert(
            `✅ Datos de Firestore eliminados exitosamente.\n\n` +
            `⚠️ No se pudo eliminar el usuario de Firebase Authentication automáticamente.\n` +
            `Por favor, elimínalo manualmente desde Firebase Console.\n\n` +
            `UID: ${clienteUid}\n` +
            `Email: ${solicitud.email}`
          );
        }
      } else {
        alert(
          `✅ Solicitud eliminada exitosamente.\n\n` +
          `No se encontró un usuario creado para este cliente, por lo que solo se eliminó la solicitud.`
        );
      }
      
      // Recargar lista
      await cargarSolicitudes();
      setSolicitudSeleccionada(null);
      
    } catch (error) {
      console.error('❌ Error eliminando cliente:', error);
      alert(`❌ Error al eliminar el cliente: ${error.message}\n\nPor favor, revisa la consola para más detalles.`);
    } finally {
      setEliminandoCliente(false);
    }
  };

  const guardarPasswordYRecrear = async () => {
    if (!nuevaPassword || nuevaPassword.length < 6) {
      alert('❌ La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (nuevaPassword !== confirmarPassword) {
      alert('❌ Las contraseñas no coinciden');
      return;
    }

    if (!solicitudSeleccionada) {
      alert('❌ Error: No se encontró la solicitud');
      return;
    }

    try {
      setVerificandoAuth(true);

      // Codificar la contraseña en base64
      const passwordHash = btoa(nuevaPassword);

      // Actualizar la solicitud con el password_hash
      await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
        password_hash: passwordHash,
        password_actualizado_por_admin: true,
        fecha_actualizacion_password: new Date()
      });

      // Actualizar solicitudSeleccionada con el nuevo password_hash
      const solicitudActualizada = {
        ...solicitudSeleccionada,
        password_hash: passwordHash
      };

      // Intentar recrear el usuario
      const resultado = await UserCreationService.recreateUserInAuth(solicitudActualizada);

      if (resultado.success) {
        // Cerrar modal
        setShowPasswordModal(false);
        setNuevaPassword('');
        setConfirmarPassword('');

        // Actualizar la solicitud con el nuevo UID
        await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
          uid_usuario: resultado.uid,
          usuario_creado: true
        });
        
        await cargarSolicitudes();
        setSolicitudSeleccionada({ 
          ...solicitudActualizada, 
          uid_usuario: resultado.uid, 
          usuario_creado: true 
        });

        alert(`✅ Contraseña guardada y usuario recreado exitosamente.\nUID: ${resultado.uid}\nEmail: ${resultado.email}`);
      } else {
        // Si aún hay error, intentar con createClientUser
        const resultadoCreacion = await UserCreationService.createClientUser(solicitudActualizada);
        if (resultadoCreacion.success) {
          setShowPasswordModal(false);
          setNuevaPassword('');
          setConfirmarPassword('');
          
          // Actualizar la solicitud con el UID
          await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
            uid_usuario: resultadoCreacion.uid,
            usuario_creado: true,
            password_hash: passwordHash
          });
          
          await cargarSolicitudes();
          setSolicitudSeleccionada({ 
            ...solicitudActualizada, 
            uid_usuario: resultadoCreacion.uid, 
            usuario_creado: true 
          });
          
          const mensaje = resultadoCreacion.usuarioYaExistia 
            ? `✅ Contraseña guardada. El usuario ya existía en Firebase Auth y se actualizó la información.\nUID: ${resultadoCreacion.uid}\nEmail: ${resultadoCreacion.email}`
            : `✅ Contraseña guardada y usuario creado exitosamente.\nUID: ${resultadoCreacion.uid}\nEmail: ${resultadoCreacion.email}`;
          
          alert(mensaje);
        } else {
          // Si el error es que el email ya está en uso, informar al usuario
          if (resultadoCreacion.code === 'auth/email-already-in-use' || resultadoCreacion.emailExists) {
            alert(`⚠️ El email ya está registrado en Firebase Auth.\n\nEl usuario puede intentar iniciar sesión o restablecer su contraseña.\n\nSi necesitas actualizar la información, usa la opción "Revalidar Login".`);
          } else {
            alert(`❌ Error al crear usuario: ${resultadoCreacion.error}`);
          }
        }
      }
    } catch (error) {
      console.error('Error guardando contraseña y recreando usuario:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setVerificandoAuth(false);
    }
  };

  const recrearUsuarioEnAuth = async () => {
    if (!solicitudSeleccionada) return;
    
    setVerificandoAuth(true);
    try {
      const resultado = await UserCreationService.recreateUserInAuth(solicitudSeleccionada);
      if (resultado.success) {
        const mensaje = resultado.usuarioYaExistia
          ? `✅ El usuario ya existía en Firebase Auth. Se actualizó la información.\nUID: ${resultado.uid}\nEmail: ${resultado.email}`
          : `✅ Usuario recreado exitosamente en Firebase Auth.\nUID: ${resultado.uid}\nEmail: ${resultado.email}`;
        
        alert(mensaje);
        
        // Actualizar la solicitud con el UID
        await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
          uid_usuario: resultado.uid,
          usuario_creado: true
        });
        await cargarSolicitudes();
        setSolicitudSeleccionada({ ...solicitudSeleccionada, uid_usuario: resultado.uid, usuario_creado: true });
      } else {
        // Si el error es que no hay password_hash, mostrar modal para ingresar contraseña
        if (resultado.error && resultado.error.includes('No se encontró password_hash')) {
          setShowPasswordModal(true);
        } else if (resultado.code === 'auth/email-already-in-use' || resultado.emailExists) {
          // Si el email ya está en uso, informar y buscar el UID existente
          alert(`⚠️ El email ya está registrado en Firebase Auth.\n\nEl usuario puede intentar iniciar sesión o restablecer su contraseña.`);
          
          // Buscar el UID del usuario existente
          try {
            const usuariosQuery = query(
              collection(db, 'usuarios'),
              where('email', '==', solicitudSeleccionada.email),
              limit(1)
            );
            const usuariosSnapshot = await getDocs(usuariosQuery);
            
            if (!usuariosSnapshot.empty) {
              const usuarioDoc = usuariosSnapshot.docs[0];
              const usuarioData = usuarioDoc.data();
              const uidExistente = usuarioData.uid || usuarioDoc.id;
              
              // Actualizar la solicitud con el UID existente
              await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
                uid_usuario: uidExistente,
                usuario_creado: true
              });
              await cargarSolicitudes();
              setSolicitudSeleccionada({ 
                ...solicitudSeleccionada, 
                uid_usuario: uidExistente, 
                usuario_creado: true 
              });
              
              alert(`✅ Se actualizó la solicitud con el UID del usuario existente: ${uidExistente}`);
            }
          } catch (error) {
            console.error('Error buscando usuario existente:', error);
          }
        } else {
          alert(`❌ Error: ${resultado.error}`);
        }
      }
    } catch (error) {
      console.error('Error recreando usuario:', error);
      // Si el error es que no hay password_hash, mostrar modal
      if (error.message && error.message.includes('password_hash')) {
        setShowPasswordModal(true);
      } else if (error.code === 'auth/email-already-in-use') {
        // Si el error es que el email ya está en uso, buscar el UID existente
        try {
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', solicitudSeleccionada.email),
            limit(1)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          
          if (!usuariosSnapshot.empty) {
            const usuarioDoc = usuariosSnapshot.docs[0];
            const usuarioData = usuarioDoc.data();
            const uidExistente = usuarioData.uid || usuarioDoc.id;
            
            await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
              uid_usuario: uidExistente,
              usuario_creado: true
            });
            await cargarSolicitudes();
            setSolicitudSeleccionada({ 
              ...solicitudSeleccionada, 
              uid_usuario: uidExistente, 
              usuario_creado: true 
            });
            
            alert(`✅ El usuario ya existía en Firebase Auth. Se actualizó la solicitud con el UID: ${uidExistente}`);
          } else {
            alert(`⚠️ El email ya está registrado en Firebase Auth pero no se encontró en la base de datos.`);
          }
        } catch (searchError) {
          console.error('Error buscando usuario existente:', searchError);
          alert(`❌ Error: ${error.message}`);
        }
      } else {
        alert(`❌ Error: ${error.message}`);
      }
    } finally {
      setVerificandoAuth(false);
    }
  };

  const enviarRestablecimientoContrasena = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`✅ Email de restablecimiento de contraseña enviado a ${email}`);
    } catch (error) {
      console.error('Error enviando email de restablecimiento:', error);
      if (error.code === 'auth/user-not-found') {
        alert('❌ El usuario no existe en Firebase Auth. Debes recrearlo primero.');
      } else {
        alert(`❌ Error: ${error.message}`);
      }
    }
  };

  const [showRevalidarModal, setShowRevalidarModal] = useState(false);
  const [revalidacionData, setRevalidacionData] = useState(null);

  const handleRevalidarLogin = async (solicitud) => {
    if (!solicitud) return;
    
    setVerificandoAuth(true);
    setRevalidacionData(null);
    
    try {
      const email = solicitud.email;
      const uid = solicitud.uid_usuario;
      
      // 1. Verificar si existe en Firebase Auth
      let existeEnAuth = false;
      let metodosAuth = [];
      try {
        const authCheck = await UserCreationService.checkUserExistsInAuth(email);
        existeEnAuth = authCheck.exists;
        metodosAuth = authCheck.methods || [];
      } catch (authError) {
        console.error('Error verificando Firebase Auth:', authError);
      }

      // 2. Verificar si existe en colección usuarios
      let existeEnUsuarios = false;
      let datosUsuario = null;
      try {
        if (uid) {
          const usuarioDoc = await getDoc(doc(db, 'usuarios', uid));
          if (usuarioDoc.exists()) {
            existeEnUsuarios = true;
            datosUsuario = usuarioDoc.data();
          }
        }
        
        // Si no se encontró por UID, buscar por email
        if (!existeEnUsuarios && email) {
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', email),
            limit(1)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          if (!usuariosSnapshot.empty) {
            existeEnUsuarios = true;
            datosUsuario = usuariosSnapshot.docs[0].data();
          }
        }
      } catch (usuariosError) {
        console.error('Error verificando colección usuarios:', usuariosError);
      }

      // 3. Buscar contraseña temporal en solicitudes_cliente
      let tienePasswordTemporal = false;
      let passwordTemporal = null;
      try {
        const solicitudesQuery = query(
          collection(db, 'solicitudes_cliente'),
          where('email', '==', email),
          where('usuario_creado', '==', true),
          limit(1)
        );
        const solicitudesSnapshot = await getDocs(solicitudesQuery);
        
        if (!solicitudesSnapshot.empty) {
          const solicitudData = solicitudesSnapshot.docs[0].data();
          if (solicitudData.password_temporal_generado && solicitudData.password_temporal) {
            tienePasswordTemporal = true;
            passwordTemporal = solicitudData.password_temporal;
          }
        }
      } catch (solicitudError) {
        console.error('Error buscando solicitud:', solicitudError);
      }

      // 4. Preparar datos de revalidación
      setRevalidacionData({
        solicitud,
        email,
        uid,
        existeEnAuth,
        metodosAuth,
        existeEnUsuarios,
        datosUsuario,
        tienePasswordTemporal,
        passwordTemporal,
        puedeHacerLogin: existeEnAuth && existeEnUsuarios
      });

      setShowRevalidarModal(true);
    } catch (error) {
      console.error('Error en revalidación:', error);
      alert('❌ Error al revalidar el login. Por favor, intenta nuevamente.');
    } finally {
      setVerificandoAuth(false);
    }
  };

  const ejecutarRevalidacion = async (accion) => {
    if (!revalidacionData) return;
    
    setVerificandoAuth(true);
    try {
      switch (accion) {
        case 'enviar_reset':
          await enviarRestablecimientoContrasena(revalidacionData.email);
          break;
          
        case 'recrear_usuario':
          await recrearUsuarioEnAuth();
          setShowRevalidarModal(false);
          break;
          
        case 'resetear_password':
          // Asegurar que la solicitud esté seleccionada para el modal de contraseña
          if (!solicitudSeleccionada && revalidacionData.solicitud) {
            setSolicitudSeleccionada(revalidacionData.solicitud);
          }
          setShowPasswordModal(true);
          setShowRevalidarModal(false);
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error('Error ejecutando revalidación:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setVerificandoAuth(false);
    }
  };

  const solicitudesFiltradas = solicitudes.filter(solicitud => {
    if (filter === 'all') return true;
    return solicitud.estado_general === filter;
  });

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'enviada': return 'bg-blue-100 text-blue-800';
      case 'en_revision': return 'bg-yellow-100 text-yellow-800';
      case 'aprobada': return 'bg-green-100 text-green-800';
      case 'rechazada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (estado) => {
    const labels = { enviada: 'Enviada', en_revision: 'En revisión', aprobada: 'Aprobada', rechazada: 'Rechazada' };
    return labels[estado] || (estado || 'Pendiente');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Solicitudes de Cliente</h1>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="all">Todas las solicitudes</option>
            <option value="enviada">Enviadas</option>
            <option value="en_revision">En revisión</option>
            <option value="aprobada">Aprobadas</option>
            <option value="rechazada">Rechazadas</option>
          </select>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: solicitudes.length, color: 'bg-blue-500' },
          { label: 'Pendientes', value: solicitudes.filter(s => s.estado_general === 'enviada').length, color: 'bg-yellow-500' },
          { label: 'En Revisión', value: solicitudes.filter(s => s.estado_general === 'en_revision').length, color: 'bg-orange-500' },
          { label: 'Aprobadas', value: solicitudes.filter(s => s.estado_general === 'aprobada').length, color: 'bg-green-500' }
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className={`w-4 h-4 rounded-full ${stat.color} mr-3`}></div>
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lista de solicitudes */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Etapa Actual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progreso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {solicitudesFiltradas.map((solicitud) => (
                <tr key={solicitud.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {solicitud.nombres} {solicitud.apellidos}
                        </span>
                        {solicitud.intentoAcceder && (
                          <span
                            title={`Intentó acceder${solicitud.contadorIntentos > 1 ? ` ${solicitud.contadorIntentos} veces` : ''}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-orange-100 text-orange-700 border border-orange-200"
                          >
                            🔥 Ansioso{solicitud.contadorIntentos > 1 ? ` ×${solicitud.contadorIntentos}` : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{solicitud.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit px-2 py-1 text-xs font-semibold rounded-full ${getEstadoColor(solicitud.estado_general)}`}>
                        {getEstadoLabel(solicitud.estado_general)}
                      </span>
                      {(solicitud.codigoRechazo === DUPLICATE_EMAIL_CODE ||
                        solicitud.codigo_rechazo === DUPLICATE_EMAIL_CODE) && (
                        <span className="inline-flex w-fit px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-red-700 text-white">
                          Correo ya registrado
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {solicitud.etapa_actual?.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${solicitud.progreso_porcentaje || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{solicitud.progreso_porcentaje || 0}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {solicitud.fecha_solicitud?.toDate ? 
                      solicitud.fecha_solicitud.toDate().toLocaleDateString() : 
                      'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSolicitudSeleccionada(solicitud)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Ver Detalles
                      </button>
                      <button
                        onClick={() => eliminarClienteCompleto(solicitud)}
                        disabled={eliminandoCliente}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Eliminar cliente completamente"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de detalles mejorado */}
      {solicitudSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            {/* Header del modal */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-1">
                    Detalle de Solicitud
                  </h3>
                  <p className="text-purple-100">
                    {solicitudSeleccionada.nombres} {solicitudSeleccionada.apellidos}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => eliminarClienteCompleto(solicitudSeleccionada)}
                    disabled={eliminandoCliente}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                    title="Eliminar cliente completamente"
                  >
                    {eliminandoCliente ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Eliminando...
                      </>
                    ) : (
                      <>
                        🗑️ Eliminar Cliente
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setSolicitudSeleccionada(null)}
                    className="text-white hover:text-gray-200 text-2xl font-bold w-10 h-10 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 transition-all"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              {/* Estado general */}
              <div className="mt-4 flex items-center gap-3">
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  solicitudSeleccionada.estado_general === 'aprobada' ? 'bg-green-500 text-white' :
                  solicitudSeleccionada.estado_general === 'en_revision' ? 'bg-yellow-500 text-white' :
                  solicitudSeleccionada.estado_general === 'rechazada' ? 'bg-red-500 text-white' :
                  'bg-blue-500 text-white'
                }`}>
                  {getEstadoLabel(solicitudSeleccionada.estado_general)}
                </span>
                {solicitudSeleccionada.usuario_creado && (
                  <span className="px-4 py-2 bg-green-500 text-white rounded-full text-sm font-semibold">
                    ✓ Usuario Creado
                  </span>
                )}
                {solicitudSeleccionada.email_activacion_enviado && (
                  <span className="px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-semibold flex items-center gap-1">
                    📧 Email Enviado
                  </span>
                )}
                {solicitudSeleccionada.usuario_creado && !solicitudSeleccionada.email_activacion_enviado && (
                  <span className="px-4 py-2 bg-yellow-500 text-white rounded-full text-sm font-semibold flex items-center gap-1">
                    ⚠️ Email No Enviado
                  </span>
                )}
                {solicitudSeleccionada.whatsapp_activacion_enviado && (
                  <span className="px-4 py-2 bg-green-600 text-white rounded-full text-sm font-semibold flex items-center gap-1">
                    📱 WhatsApp Enviado
                  </span>
                )}
                <div className="flex-1 bg-white bg-opacity-20 rounded-full h-2">
                  <div 
                    className="bg-white h-2 rounded-full transition-all" 
                    style={{ width: `${solicitudSeleccionada.progreso_porcentaje || 0}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">{solicitudSeleccionada.progreso_porcentaje || 0}%</span>
              </div>

              {(solicitudSeleccionada.codigoRechazo === DUPLICATE_EMAIL_CODE ||
                solicitudSeleccionada.codigo_rechazo === DUPLICATE_EMAIL_CODE) && (
                <div
                  className="mt-4 rounded-lg border-2 border-red-400 bg-white p-4 text-red-900 shadow-md"
                  role="alert"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-red-600 px-3 py-1 text-xs font-bold uppercase text-white">
                      Rechazada
                    </span>
                    <span className="rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                      Correo duplicado
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold">
                    {solicitudSeleccionada.motivoRechazo ||
                      solicitudSeleccionada.motivo_rechazo ||
                      'El correo electrónico ya se encuentra registrado en el sistema.'}
                  </p>
                  <p className="mt-1 text-xs text-red-800">
                    Se intentó notificar al solicitante por correo. Para recuperar acceso debe usar la cuenta existente o escribir a contacto@kawescar.cl.
                  </p>
                  {solicitudSeleccionada.rejectedBy && (
                    <p className="mt-2 text-xs text-red-700">
                      Gestionado por:{' '}
                      <strong>
                        {solicitudSeleccionada.rejectedBy.nombre ||
                          solicitudSeleccionada.rejectedBy.email ||
                          solicitudSeleccionada.rejectedBy.uid}
                      </strong>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Contenido del modal */}
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Información del cliente - Mejorado */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-100">
                    <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">👤</span>
                      Información Personal
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">RUT</p>
                        <p className="font-semibold text-gray-900">{solicitudSeleccionada.rut || 'No especificado'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="font-semibold text-gray-900">{solicitudSeleccionada.email}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Teléfono</p>
                        <p className="font-semibold text-gray-900">{solicitudSeleccionada.telefono || 'No especificado'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Dirección</p>
                        <p className="font-semibold text-gray-900">{solicitudSeleccionada.direccion || 'No especificado'}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Comuna</p>
                          <p className="font-semibold text-gray-900">{solicitudSeleccionada.comuna || 'N/A'}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Región</p>
                          <p className="font-semibold text-gray-900">{solicitudSeleccionada.region || 'N/A'}</p>
                        </div>
                      </div>
                      {solicitudSeleccionada.profesion && (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Profesión</p>
                          <p className="font-semibold text-gray-900">{solicitudSeleccionada.profesion}</p>
                        </div>
                      )}
                      {solicitudSeleccionada.empresa && (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Empresa</p>
                          <p className="font-semibold text-gray-900">{solicitudSeleccionada.empresa}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {solicitudSeleccionada.servicios_interes?.length > 0 && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-100">
                      <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="text-xl">🔧</span>
                        Servicios de Interés
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {solicitudSeleccionada.servicios_interes.map(servicio => (
                          <span key={servicio} className="bg-green-500 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                            {servicio}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {solicitudSeleccionada.motivo_registro && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-100">
                      <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="text-xl">💭</span>
                        Motivo de Registro
                      </h5>
                      <p className="text-sm text-gray-700 leading-relaxed">{solicitudSeleccionada.motivo_registro}</p>
                    </div>
                  )}
                </div>

                {/* Gestión de etapas - Mejorado */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6 border-2 border-orange-100">
                    <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">📋</span>
                      Gestión de Etapas
                    </h4>
                    
                    {/* Etapas */}
                    {['validacion_datos'].map((etapaKey) => {
                      const etapa = solicitudSeleccionada.etapas?.[etapaKey] || { estado: 'pendiente' };
                      const etapaNombres = {
                        validacion_datos: 'Validación de Datos'
                      };

                      const isCompletada = etapa.estado === 'completada';
                      const isRechazada = etapa.estado === 'rechazada';
                      const isPendiente = etapa.estado === 'pendiente' || !etapa.estado;

                      return (
                        <div 
                          key={etapaKey} 
                          className={`mb-4 rounded-xl p-4 border-2 transition-all ${
                            isCompletada ? 'bg-green-50 border-green-400 shadow-md' :
                            isRechazada ? 'bg-red-50 border-red-400 shadow-md' :
                            'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h5 className={`font-bold mb-1 flex items-center gap-2 transition-colors ${
                                isCompletada ? 'text-green-700' :
                                isRechazada ? 'text-red-700' :
                                'text-gray-900'
                              }`}>
                                {isCompletada && <span className="text-green-600 text-xl">✓</span>}
                                {isRechazada && <span className="text-red-600 text-xl">✗</span>}
                                {isPendiente && <span className="text-gray-400">○</span>}
                                {etapaNombres[etapaKey]}
                              </h5>
                              {etapaKey === 'validacion_datos' && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Revisar datos personales, documentos de identidad y dirección
                                </p>
                              )}
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              isCompletada ? 'bg-green-500 text-white' :
                              isRechazada ? 'bg-red-500 text-white' :
                              'bg-gray-300 text-gray-700'
                            }`}>
                              {etapa.estado || 'pendiente'}
                            </span>
                          </div>
                          
                          {isPendiente && (
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => actualizarEtapa(solicitudSeleccionada.id, etapaKey, 'completada')}
                                disabled={procesandoEtapa || isCompletada}
                                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-md ${
                                  isCompletada || botonesPresionados[`${solicitudSeleccionada.id}_${etapaKey}_completada`]
                                    ? 'bg-gradient-to-r from-green-800 to-green-900 text-white cursor-default'
                                    : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
                                }`}
                              >
                                {procesandoEtapa && !isCompletada ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Procesando...
                                  </span>
                                ) : isCompletada || botonesPresionados[`${solicitudSeleccionada.id}_${etapaKey}_completada`] ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <span>✓</span>
                                    <span>Completada</span>
                                  </span>
                                ) : (
                                  <span className="flex items-center justify-center gap-2">
                                    <span>✓</span>
                                    <span>Completar</span>
                                  </span>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  const comentario = prompt('Ingresa el motivo del rechazo:');
                                  if (comentario) {
                                    actualizarEtapa(solicitudSeleccionada.id, etapaKey, 'rechazada', comentario);
                                  }
                                }}
                                disabled={procesandoEtapa || isRechazada}
                                className={`px-4 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-md ${
                                  isRechazada || botonesPresionados[`${solicitudSeleccionada.id}_${etapaKey}_rechazada`]
                                    ? 'bg-gradient-to-r from-red-800 to-red-900 text-white cursor-default'
                                    : 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
                                }`}
                              >
                                {procesandoEtapa && !isRechazada ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Procesando...
                                  </span>
                                ) : isRechazada || botonesPresionados[`${solicitudSeleccionada.id}_${etapaKey}_rechazada`] ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <span>✗</span>
                                    <span>Rechazada</span>
                                  </span>
                                ) : (
                                  <span className="flex items-center justify-center gap-2">
                                    <span>✗</span>
                                    <span>Rechazar</span>
                                  </span>
                                )}
                              </button>
                            </div>
                          )}

                          {etapa.comentarios && (
                            <div className="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-700">
                              <strong>Comentarios:</strong> {etapa.comentarios}
                            </div>
                          )}

                          {etapa.responsable && (
                            <p className="text-xs text-gray-500 mt-2">
                              Por: {etapa.responsable}
                            </p>
                          )}

                          {etapa.fecha_fin && (
                            <p className="text-xs text-gray-500 mt-1">
                              Fecha: {etapa.fecha_fin.toDate ? etapa.fecha_fin.toDate().toLocaleString() : new Date(etapa.fecha_fin).toLocaleString()}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Botón de activación rápida */}
                  {!solicitudSeleccionada.usuario_creado && 
                   solicitudSeleccionada.etapas?.validacion_datos?.estado === 'completada' && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-300">
                      <h5 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                        <span className="text-xl">🚀</span>
                        Activar Solicitud
                      </h5>
                      <p className="text-sm text-gray-700 mb-4">
                        La validación de datos está completa. Puedes activar esta solicitud para crear la cuenta del cliente.
                      </p>
                      <button
                        onClick={() => activarSolicitud(solicitudSeleccionada)}
                        disabled={activandoSolicitud || procesandoEtapa}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-bold text-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                      >
                        {activandoSolicitud ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Activando...</span>
                          </>
                        ) : (
                          <>
                            <span>✓</span>
                            <span>Activar Solicitud y Crear Usuario</span>
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-600 mt-3 text-center">
                        Esto creará la cuenta en Firebase Auth y el perfil en Firestore
                      </p>
                    </div>
                  )}

                  {/* Información del usuario creado */}
                  {solicitudSeleccionada.usuario_creado && solicitudSeleccionada.uid_usuario && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-300">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-bold text-green-800 flex items-center gap-2">
                          <span className="text-xl">✅</span>
                          Usuario Activado
                        </h5>
                        <div className="flex gap-2">
                          <button
                            onClick={() => verificarUsuarioEnAuth(solicitudSeleccionada.email)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-semibold flex items-center gap-2"
                            title="Verificar estado de autenticación"
                          >
                            🔍 Verificar Estado
                          </button>
                          <button
                            onClick={() => handleRevalidarLogin(solicitudSeleccionada)}
                            disabled={verificandoAuth}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Revalidar inicio de sesión del cliente"
                          >
                            {verificandoAuth ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Revalidando...
                              </>
                            ) : (
                              <>
                                🔐 Revalidar Login
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-semibold text-gray-700">UID:</span> <span className="font-mono text-xs">{solicitudSeleccionada.uid_usuario}</span></p>
                        <p><span className="font-semibold text-gray-700">Perfil ID:</span> <span className="font-mono text-xs">{solicitudSeleccionada.perfil_id}</span></p>
                        {solicitudSeleccionada.fecha_activacion && (
                          <p><span className="font-semibold text-gray-700">Fecha Activación:</span> {
                            solicitudSeleccionada.fecha_activacion.toDate ? 
                              solicitudSeleccionada.fecha_activacion.toDate().toLocaleString() : 
                              new Date(solicitudSeleccionada.fecha_activacion).toLocaleString()
                          }</p>
                        )}
                        {solicitudSeleccionada.activado_por && (
                          <p><span className="font-semibold text-gray-700">Activado por:</span> {solicitudSeleccionada.activado_por}</p>
                        )}
                        {solicitudSeleccionada.password_temporal && (
                          <div className="mt-3 bg-blue-50 border-2 border-blue-300 rounded p-3">
                            <p className="text-xs text-blue-700 font-semibold mb-1">🔑 Contraseña Temporal:</p>
                            <p className="font-mono font-bold text-blue-900 text-center">{solicitudSeleccionada.password_temporal}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Comunicación: email informado, email de bienvenida y WhatsApp */}
                      {solicitudSeleccionada.usuario_creado && (
                        <div className={`mt-4 p-4 rounded-lg border-l-4 ${
                          solicitudSeleccionada.email_activacion_enviado 
                            ? 'bg-blue-50 border-blue-500' 
                            : 'bg-yellow-50 border-yellow-500'
                        }`}>
                          <p className="text-xs text-gray-600 mb-3">
                            <span className="font-semibold">Email informado por el cliente:</span>{' '}
                            <span className="font-mono text-gray-900">{solicitudSeleccionada.email || '—'}</span>
                          </p>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm flex items-center gap-2">
                              📧 Email de bienvenida / activación:
                              {solicitudSeleccionada.email_activacion_enviado ? (
                                <span className="text-green-600">✅ Enviado</span>
                              ) : (
                                <span className="text-red-600">❌ No Enviado</span>
                              )}
                            </span>
                          </div>
                          
                          {solicitudSeleccionada.email_activacion_enviado ? (
                            <div className="space-y-1 text-xs text-gray-600">
                              {solicitudSeleccionada.email_activacion_fecha && (
                                <p>
                                  <span className="font-semibold">Fecha:</span> {
                                    solicitudSeleccionada.email_activacion_fecha.toDate ? 
                                      solicitudSeleccionada.email_activacion_fecha.toDate().toLocaleString() : 
                                      new Date(solicitudSeleccionada.email_activacion_fecha).toLocaleString()
                                  }
                                </p>
                              )}
                              {solicitudSeleccionada.email_activacion_tipo && (
                                <p>
                                  <span className="font-semibold">Tipo:</span> {
                                    solicitudSeleccionada.email_activacion_tipo === 'password_reset' || solicitudSeleccionada.email_activacion_tipo === 'password_reset_fallback'
                                      ? 'Link para establecer contraseña'
                                      : 'Email de bienvenida'
                                  }
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {solicitudSeleccionada.email_activacion_error && (
                                <p className="text-xs text-red-600">
                                  <span className="font-semibold">Error:</span> {solicitudSeleccionada.email_activacion_error}
                                </p>
                              )}
                              {solicitudSeleccionada.email_activacion_intentos && (
                                <p className="text-xs text-gray-600">
                                  <span className="font-semibold">Intentos fallidos:</span> {solicitudSeleccionada.email_activacion_intentos}
                                </p>
                              )}
                              <div className="space-y-2">
                                <button
                                  onClick={async () => {
                                    if (!confirm('¿Deseas reenviar el correo de bienvenida a la comunidad KawesCar (con enlace de acceso al login)?')) return;
                                    
                                    try {
                                      const functions = getFunctions();
                                      const enviarBienvenida = httpsCallable(functions, 'enviarEmailBienvenidaCliente');
                                      const loginUrl = `${window.location.origin}/login`;
                                      await enviarBienvenida({
                                        email: solicitudSeleccionada.email,
                                        nombre: [solicitudSeleccionada.nombres, solicitudSeleccionada.apellidos].filter(Boolean).join(' ') || solicitudSeleccionada.email,
                                        loginUrl
                                      });
                                      
                                      // Actualizar estado en la solicitud
                                      await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
                                        email_activacion_enviado: true,
                                        email_activacion_fecha: new Date(),
                                        email_activacion_tipo: 'bienvenida',
                                        email_activacion_error: null
                                      });
                                      
                                      // Actualizar estado local
                                      setSolicitudSeleccionada({
                                        ...solicitudSeleccionada,
                                        email_activacion_enviado: true,
                                        email_activacion_fecha: new Date(),
                                        email_activacion_tipo: 'bienvenida',
                                        email_activacion_error: null
                                      });
                                      
                                      alert('✅ Correo de bienvenida reenviado (con enlace de acceso al login)');
                                    } catch (error) {
                                      console.error('Error reenviando correo de bienvenida:', error);
                                      alert(`❌ Error al reenviar correo: ${error.message || error.code || 'Error desconocido'}`);
                                    }
                                  }}
                                  className="w-full bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 font-semibold flex items-center justify-center gap-2"
                                >
                                  📧 Reenviar correo de bienvenida
                                </button>
                                
                                {/* Botón para reenviar correo con credenciales */}
                                {solicitudSeleccionada.usuario_creado && solicitudSeleccionada.password_temporal && (
                                  <button
                                    onClick={async () => {
                                      if (!confirm('¿Deseas enviar un email con las credenciales de inicio de sesión al cliente?')) return;
                                      
                                      try {
                                        const loginUrl = `${window.location.origin}/login`;
                                        const emailBody = `
Estimado/a ${solicitudSeleccionada.nombres} ${solicitudSeleccionada.apellidos},

¡Bienvenido a KawesCar!

Tu cuenta ha sido activada exitosamente. A continuación encontrarás tus credenciales de acceso:

📧 Email: ${solicitudSeleccionada.email}
🔑 Contraseña temporal: ${solicitudSeleccionada.password_temporal}

🔗 URL de inicio de sesión: ${loginUrl}

IMPORTANTE:
- Por seguridad, te recomendamos cambiar tu contraseña después del primer inicio de sesión.
- Puedes iniciar sesión haciendo clic en el siguiente enlace: ${loginUrl}

Si tienes alguna pregunta, no dudes en contactarnos.

¡Gracias por unirte a nuestra comunidad!

Equipo KawesCar
                                        `;
                                        
                                        // Intentar usar Cloud Function si existe, sino usar window.location para abrir cliente de email
                                        try {
                                          const functions = getFunctions();
                                          const enviarEmailCredenciales = httpsCallable(functions, 'enviarEmailCredencialesCliente');
                                          
                                          await enviarEmailCredenciales({
                                            to: solicitudSeleccionada.email,
                                            nombre: `${solicitudSeleccionada.nombres} ${solicitudSeleccionada.apellidos}`,
                                            email: solicitudSeleccionada.email,
                                            password: solicitudSeleccionada.password_temporal,
                                            loginUrl: loginUrl
                                          });
                                          
                                          alert('✅ Email con credenciales enviado exitosamente');
                                        } catch (cfError) {
                                          // Fallback: abrir cliente de email
                                          const subject = encodeURIComponent('Credenciales de acceso - KawesCar');
                                          const body = encodeURIComponent(emailBody);
                                          window.location.href = `mailto:${solicitudSeleccionada.email}?subject=${subject}&body=${body}`;
                                          
                                          // También copiar al portapapeles
                                          await navigator.clipboard.writeText(emailBody);
                                          alert('✅ Se abrió tu cliente de email. El contenido también se copió al portapapeles.\n\nSi prefieres, puedes copiar y pegar el mensaje manualmente.');
                                        }
                                        
                                        // Actualizar estado
                                        await updateDoc(doc(db, 'solicitudes_cliente', solicitudSeleccionada.id), {
                                          email_credenciales_enviado: true,
                                          email_credenciales_fecha: new Date()
                                        });
                                        
                                      } catch (error) {
                                        console.error('Error enviando email con credenciales:', error);
                                        alert(`❌ Error: ${error.message}`);
                                      }
                                    }}
                                    className="w-full bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 font-semibold flex items-center justify-center gap-2"
                                  >
                                    📨 Enviar Email con Credenciales
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-600">
                              <span className="font-semibold">📱 WhatsApp (aviso cuenta activada):</span>{' '}
                              {solicitudSeleccionada.whatsapp_activacion_enviado ? (
                                <span className="text-green-600">
                                  Enviado
                                  {solicitudSeleccionada.whatsapp_activacion_fecha && (
                                    <span className="text-gray-600">
                                      {' '}({solicitudSeleccionada.whatsapp_activacion_fecha.toDate ? solicitudSeleccionada.whatsapp_activacion_fecha.toDate().toLocaleString() : new Date(solicitudSeleccionada.whatsapp_activacion_fecha).toLocaleString()})
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-gray-500">No enviado</span>
                              )}
                            </p>
                          </div>

                          {/* Estado del cliente: cambió contraseña e inició sesión */}
                          {solicitudSeleccionada.usuario_creado && (
                            <div className="mt-4 p-4 rounded-lg border-l-4 bg-slate-50 border-slate-400">
                              <span className="font-semibold text-sm flex items-center gap-2 mb-2">
                                👤 Estado del cliente
                              </span>
                              {loadingEstadoCliente ? (
                                <p className="text-xs text-gray-500">Cargando...</p>
                              ) : estadoClienteUsuario ? (
                                <div className="space-y-1 text-sm">
                                  <p>
                                    <span className="font-semibold text-gray-700">Cambió contraseña:</span>{' '}
                                    {estadoClienteUsuario.passwordEstablecidaAt ? (
                                      <span className="text-green-600">
                                        Sí ({estadoClienteUsuario.passwordEstablecidaAt?.toDate?.()?.toLocaleString?.() ?? (estadoClienteUsuario.passwordEstablecidaAt && new Date(estadoClienteUsuario.passwordEstablecidaAt).toLocaleString()) ?? '—'})
                                      </span>
                                    ) : (
                                      <span className="text-amber-600">No</span>
                                    )}
                                  </p>
                                  <p>
                                    <span className="font-semibold text-gray-700">Inició sesión:</span>{' '}
                                    {estadoClienteUsuario.primerInicioSesionAt ? (
                                      <span className="text-green-600">
                                        Sí ({estadoClienteUsuario.primerInicioSesionAt?.toDate?.()?.toLocaleString?.() ?? (estadoClienteUsuario.primerInicioSesionAt && new Date(estadoClienteUsuario.primerInicioSesionAt).toLocaleString()) ?? '—'})
                                      </span>
                                    ) : (
                                      <span className="text-amber-600">No</span>
                                    )}
                                  </p>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500">Sin datos de actividad aún</p>
                              )}
                            </div>
                          )}

                          {/* Botón: Avisar por WhatsApp que enviamos correo */}
                          {solicitudSeleccionada.usuario_creado && solicitudSeleccionada.telefono && (solicitudSeleccionada.email_activacion_enviado || solicitudSeleccionada.email_credenciales_enviado) && (
                            <div className="mt-3">
                              <a
                                href={buildWhatsAppUrl(
                                  solicitudSeleccionada.telefono,
                                  `Hola ${solicitudSeleccionada.nombres || ''}, te enviamos un correo a tu email (${solicitudSeleccionada.email}) para que puedas activar tu cuenta y establecer tu contraseña. Revisa tu bandeja de entrada y carpeta de spam.`
                                ) || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
                              >
                                📱 Avisar por WhatsApp (enviamos correo)
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Estado de Firebase Auth */}
                      {estadoAuth !== null && (
                        <div className={`mt-4 p-3 rounded border-l-4 ${estadoAuth.existe ? 'bg-green-100 border-green-500' : 'bg-red-100 border-red-500'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm">Firebase Auth:</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${estadoAuth.existe ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                              {estadoAuth.existe ? '✅ Existe' : '❌ No existe'}
                            </span>
                          </div>
                          {estadoAuth.metodos && estadoAuth.metodos.length > 0 && (
                            <p className="text-xs text-gray-600">Métodos: {estadoAuth.metodos.join(', ')}</p>
                          )}
                          {!estadoAuth.existe && (
                            <button
                              onClick={recrearUsuarioEnAuth}
                              disabled={verificandoAuth}
                              className="mt-2 w-full bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {verificandoAuth ? '⏳ Recreando...' : '🔄 Recrear Usuario en Firebase Auth'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ingresar contraseña cuando falta password_hash */}
      {showPasswordModal && solicitudSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4 text-red-600">🔑 Ingresar Contraseña</h3>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-sm text-yellow-700">
                <strong>⚠️ No se encontró password_hash en la solicitud.</strong>
              </p>
              <p className="text-sm text-yellow-700 mt-2">
                Por favor, ingresa una contraseña para el usuario <strong>{solicitudSeleccionada.nombres} {solicitudSeleccionada.apellidos}</strong> ({solicitudSeleccionada.email}).
              </p>
              <p className="text-sm text-yellow-700 mt-2">
                Esta contraseña se guardará en la solicitud y se usará para crear/recrear el usuario en Firebase Auth.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={nuevaPassword}
                  onChange={(e) => setNuevaPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Mínimo 6 caracteres"
                  disabled={verificandoAuth}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmar Contraseña
                </label>
                <input
                  type="password"
                  value={confirmarPassword}
                  onChange={(e) => setConfirmarPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Repite la contraseña"
                  disabled={verificandoAuth}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && nuevaPassword && confirmarPassword) {
                      guardarPasswordYRecrear();
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={guardarPasswordYRecrear}
                disabled={verificandoAuth || !nuevaPassword || !confirmarPassword}
                className="flex-1 bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verificandoAuth ? '⏳ Guardando y Recreando...' : '✅ Guardar y Recrear Usuario'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNuevaPassword('');
                  setConfirmarPassword('');
                }}
                disabled={verificandoAuth}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Revalidación de Login */}
      {showRevalidarModal && revalidacionData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-1">🔐 Revalidar Inicio de Sesión</h3>
                  <p className="text-purple-100">
                    {revalidacionData.solicitud?.nombres} {revalidacionData.solicitud?.apellidos}
                  </p>
                  <p className="text-sm text-purple-200">{revalidacionData.email}</p>
                </div>
                <button
                  onClick={() => {
                    setShowRevalidarModal(false);
                    setRevalidacionData(null);
                  }}
                  className="text-white hover:text-gray-200 text-2xl font-bold w-10 h-10 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 transition-all"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Estado actual */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className={`p-4 rounded-lg border-2 ${
                  revalidacionData.existeEnAuth 
                    ? 'bg-green-50 border-green-300' 
                    : 'bg-red-50 border-red-300'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Firebase Auth</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      revalidacionData.existeEnAuth 
                        ? 'bg-green-200 text-green-800' 
                        : 'bg-red-200 text-red-800'
                    }`}>
                      {revalidacionData.existeEnAuth ? '✅ Existe' : '❌ No existe'}
                    </span>
                  </div>
                  {revalidacionData.metodosAuth && revalidacionData.metodosAuth.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                      Métodos: {revalidacionData.metodosAuth.join(', ')}
                    </p>
                  )}
                </div>

                <div className={`p-4 rounded-lg border-2 ${
                  revalidacionData.existeEnUsuarios 
                    ? 'bg-green-50 border-green-300' 
                    : 'bg-red-50 border-red-300'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Colección Usuarios</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      revalidacionData.existeEnUsuarios 
                        ? 'bg-green-200 text-green-800' 
                        : 'bg-red-200 text-red-800'
                    }`}>
                      {revalidacionData.existeEnUsuarios ? '✅ Existe' : '❌ No existe'}
                    </span>
                  </div>
                  {revalidacionData.uid && (
                    <p className="text-xs text-gray-600 mt-1 font-mono">
                      UID: {revalidacionData.uid}
                    </p>
                  )}
                </div>
              </div>

              {/* Estado general */}
              <div className={`p-4 rounded-lg border-2 mb-6 ${
                revalidacionData.puedeHacerLogin 
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-yellow-50 border-yellow-300'
              }`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {revalidacionData.puedeHacerLogin ? '✅' : '⚠️'}
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {revalidacionData.puedeHacerLogin 
                        ? 'El cliente puede iniciar sesión' 
                        : 'El cliente tiene problemas para iniciar sesión'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {revalidacionData.puedeHacerLogin 
                        ? 'El usuario existe tanto en Firebase Auth como en la base de datos.'
                        : 'Falta el usuario en Firebase Auth o en la base de datos. Usa las opciones abajo para solucionarlo.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contraseña temporal si existe */}
              {revalidacionData.tienePasswordTemporal && revalidacionData.passwordTemporal && (
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                  <p className="text-sm font-semibold text-blue-900 mb-2">🔑 Contraseña Temporal Disponible:</p>
                  <p className="font-mono font-bold text-blue-900 text-center text-lg bg-white p-3 rounded border-2 border-blue-400">
                    {revalidacionData.passwordTemporal}
                  </p>
                  <p className="text-xs text-blue-700 mt-2 text-center">
                    El cliente puede usar esta contraseña para iniciar sesión
                  </p>
                </div>
              )}

              {/* Acciones disponibles */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 mb-3">Acciones Disponibles:</h4>
                
                {/* Enviar email de restablecimiento */}
                {revalidacionData.existeEnAuth && (
                  <button
                    onClick={() => ejecutarRevalidacion('enviar_reset')}
                    disabled={verificandoAuth}
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    <span>📧</span>
                    <span>Enviar Email de Restablecimiento de Contraseña</span>
                  </button>
                )}

                {/* Recrear usuario */}
                {!revalidacionData.existeEnAuth && (
                  <button
                    onClick={() => ejecutarRevalidacion('recrear_usuario')}
                    disabled={verificandoAuth}
                    className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  >
                    <span>🔄</span>
                    <span>Recrear Usuario en Firebase Auth</span>
                  </button>
                )}

                {/* Resetear contraseña manualmente */}
                <button
                  onClick={() => ejecutarRevalidacion('resetear_password')}
                  disabled={verificandoAuth}
                  className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  <span>🔑</span>
                  <span>Establecer Nueva Contraseña Manualmente</span>
                </button>

                {/* Verificar estado nuevamente */}
                <button
                  onClick={() => handleRevalidarLogin(revalidacionData.solicitud)}
                  disabled={verificandoAuth}
                  className="w-full bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  <span>🔍</span>
                  <span>Verificar Estado Nuevamente</span>
                </button>
              </div>

              {/* Información adicional */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>💡 Nota:</strong> Si el cliente reporta problemas para iniciar sesión, primero verifica el estado arriba. 
                  Luego usa "Enviar Email de Restablecimiento" si el usuario existe, o "Recrear Usuario" si no existe en Firebase Auth.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de activación */}
      {mostrarConfirmacionActivacion && solicitudSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-2xl">
              <h3 className="text-2xl font-bold mb-2">🚀 Confirmar Activación de Solicitud</h3>
              <p className="text-green-100">¿Estás seguro de que deseas activar esta solicitud?</p>
            </div>
            
            <div className="p-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-bold text-gray-900 mb-3">Se realizarán las siguientes acciones:</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>Crear cuenta de usuario en Firebase Authentication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>Crear perfil de cliente en Firestore (colección perfiles_clientes)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>Completar todas las etapas de la solicitud</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>Enviar notificación al cliente sobre la activación</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>Marcar la solicitud como aprobada</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">Información del Cliente:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Nombre:</span>
                    <p className="font-semibold">{solicitudSeleccionada.nombres} {solicitudSeleccionada.apellidos}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-semibold">{solicitudSeleccionada.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">RUT:</span>
                    <p className="font-semibold">{solicitudSeleccionada.rut || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Teléfono:</span>
                    <p className="font-semibold">{solicitudSeleccionada.telefono || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {solicitudSeleccionada.telefono && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={abrirWhatsAppDespuesActivacion}
                      onChange={(e) => setAbrirWhatsAppDespuesActivacion(e.target.checked)}
                      className="mt-1 h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-800">
                      <strong>📱 Abrir WhatsApp al finalizar</strong> con el mensaje para el cliente: cuenta activada, felicitación y enlace directo al login para que pueda acceder.
                    </span>
                  </label>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={confirmarActivacion}
                  disabled={activandoSolicitud}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                >
                  {activandoSolicitud ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Activando...
                    </span>
                  ) : (
                    '✓ Confirmar y Activar'
                  )}
                </button>
                <button
                  onClick={() => setMostrarConfirmacionActivacion(false)}
                  disabled={activandoSolicitud}
                  className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
